import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { FormLabel } from "@arcadeai/design-system/rhf";
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Loader2,
  Play,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type TestStatus,
  useCustomVerifierTest,
} from "../hooks/use-custom-verifier-test";

type Props = {
  verifierUrl?: string | null;
  defaultUserId?: string;
};

const statusConfig: Record<
  TestStatus,
  {
    variant: "default" | "destructive" | "warning";
    icon: ReactNode;
    title: string;
  }
> = {
  idle: {
    variant: "default",
    icon: null,
    title: "Ready to test",
  },
  starting: {
    variant: "warning",
    icon: <Loader2 className="size-4 animate-spin" />,
    title: "Starting verification flow...",
  },
  pending: {
    variant: "warning",
    icon: <Loader2 className="size-4 animate-spin" />,
    title: "Waiting for verification",
  },
  completed: {
    variant: "default",
    icon: <CheckCircle className="size-4 text-emerald-600" />,
    title: "Verification successful",
  },
  failed: {
    variant: "destructive",
    icon: <AlertCircle className="size-4" />,
    title: "Verification failed",
  },
};

export function CustomVerifierTester({
  verifierUrl,
  defaultUserId = "",
}: Props) {
  const [userId, setUserId] = useState(defaultUserId);
  const { start, cancel, status, nextUri, canStart } = useCustomVerifierTest({
    verifierUrl,
    userId,
  });

  const isActive = status === "starting" || status === "pending";
  const config = statusConfig[status];

  useEffect(() => {
    if (status === "completed") {
      toast.success("Custom verifier confirmed the authorization.");
    }
  }, [status]);

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-5">
      <div className="space-y-1">
        <h4 className="font-semibold text-foreground">
          Test your custom verifier
        </h4>
        <p className="text-muted-foreground text-sm">
          Trigger a verification flow to confirm the redirect URL works before
          saving.
        </p>
      </div>

      <div className="space-y-2">
        <FormLabel>User ID to verify</FormLabel>
        <Input
          className="max-w-md"
          disabled={isActive}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="user@example.com"
          value={userId}
        />
      </div>

      <div className="flex items-center gap-3">
        {isActive ? (
          <Button onClick={cancel} size="sm" type="button" variant="secondary">
            <X className="mr-1.5 size-4" />
            Cancel
          </Button>
        ) : (
          <Button
            disabled={!canStart}
            onClick={start}
            size="sm"
            type="button"
            variant="outline"
          >
            <Play className="mr-1.5 size-4" />
            Run test
          </Button>
        )}
      </div>

      {status !== "idle" && (
        <Alert className="border-l-4" variant={config.variant}>
          {config.icon}
          <div className="flex-1 space-y-2">
            <AlertTitle className="font-semibold text-sm">
              {config.title}
            </AlertTitle>
            <AlertDescription>
              {status === "pending" && nextUri && (
                <div className="space-y-3">
                  <p className="text-sm">
                    Complete the authorization in the popup, or use the link
                    below.
                  </p>
                  <div className="flex items-center gap-2 rounded-md border border-border/50 bg-background/50 px-3 py-2">
                    <code
                      className="min-w-0 flex-1 truncate font-mono text-muted-foreground text-xs"
                      title={nextUri}
                    >
                      {nextUri}
                    </code>
                    <CopyButton
                      className="size-7 shrink-0"
                      content={nextUri}
                      size="sm"
                      variant="ghost"
                    />
                  </div>
                  <Button
                    className="gap-1.5"
                    render={
                      // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
                      <a
                        href={nextUri}
                        rel="noopener noreferrer"
                        target="_blank"
                      />
                    }
                    size="sm"
                    variant="outline"
                  >
                    <ExternalLink className="size-3.5" />
                    Open verification link
                  </Button>
                </div>
              )}
              {status === "starting" && (
                <p className="text-sm">Initializing the verification flow...</p>
              )}
              {status === "completed" && (
                <p className="text-emerald-700 text-sm dark:text-emerald-400">
                  Your custom verifier successfully confirmed the authorization.
                </p>
              )}
              {status === "failed" && (
                <p className="text-sm">
                  The verification test failed. Please check your verifier URL
                  and try again.
                </p>
              )}
            </AlertDescription>
          </div>
        </Alert>
      )}
    </div>
  );
}
